import { AxiosError, AxiosInstance, AxiosResponse, AxiosStatic } from 'axios';
import { toast } from '@britishcouncil/react-admin';
import { CustomAxiosRequestConfig } from 'api/planning';

const RESPONSES_WITH_ERRORS = [400, 422];

export interface ServerError {
  propertyName?: string;
  errorCode?: string;
  message: string;
}

type ErrorResponseData = {
  errors?: ResponseError[];
  error?: ResponseError[];
};

type ResponseError = {
  propertyName?: string;
  message: string;
};

//Logic of this interceptor was moved from angularJS application for backward compatibility
export function includeApiValidationErrorInterceptor(axios: AxiosInstance | AxiosStatic) {
  axios.interceptors.response.use(
    (resp: AxiosResponse<unknown>) => resp,
    async (error: AxiosError) => {
      handleError(error);
      return Promise.reject(error);
    }
  );
}

export const handleError = (error: AxiosError) => {
  if (error.response) {
    if (error.response.status === 409) {
      showErrorToast('This page has been modified. Please refresh the page to see changes.');
    } else if (
      !isResponseWithErrors(error.response) &&
      !isUnauthorizedError(error.response) &&
      !isNoPermissionToAccessError(error.response) &&
      !isCustomErrorHandling(error.response)
    ) {
      showErrorToast('Error occurred when processing your request');
    }
  }
};

const showErrorToast = (message: string) => {
  toast({ type: 'error', title: 'Error', message, displayTime: 5000, closeIcon: true });
};

const getApiErrors = (response: AxiosResponse<unknown>): Array<ServerError> | undefined => {
  if (response?.data) {
    const responseData = response.data as ErrorResponseData;
    return (responseData.errors ?? responseData.error)?.map((x) => ({
      propertyName: x.propertyName,
      message: x.message,
    }));
  }

  return undefined;
};

const isResponseWithErrors = (response: AxiosResponse<unknown>) => {
  if (!response || RESPONSES_WITH_ERRORS.indexOf(response.status) === -1) {
    return false;
  }

  return !!getApiErrors(response);
};

const isUnauthorizedError = (response: AxiosResponse<unknown>) =>
  !!response && response.status === 401;

const isNoPermissionToAccessError = (response: AxiosResponse<unknown>) =>
  !!response && response.status === 406;

const isCustomErrorHandling = (response: AxiosResponse<unknown>): boolean => {
  const config = response.config as CustomAxiosRequestConfig;
  const customErrorHandling = config.customErrorHandling;

  if (typeof customErrorHandling === 'boolean') {
    return customErrorHandling;
  }

  if (typeof customErrorHandling === 'function') {
    return customErrorHandling(response);
  }

  return false;
};
